import { createSlice, PayloadAction, createAsyncThunk, ActionReducerMapBuilder } from '@reduxjs/toolkit';

import { User } from '../../context/AuthContext';
import { errorMessage } from '../../services/apiError';
import { authService } from '../../services/authService';
import { tokenService } from '../../services/tokenService';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      const user = await authService.getCurrentUser();

      return user;
    } catch (error: unknown) {
      return rejectWithValue(errorMessage(error, 'We couldn’t load your account. Refresh and try again.'));
    }
  }
);

const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await authService.emailLogin(email, password);

      tokenService.setTokens(response.access_token, response.refresh_token);
      await dispatch(fetchCurrentUser());
    } catch (error: unknown) {
      return rejectWithValue(errorMessage(error, 'We couldn’t sign you in. Check your details and try again.'));
    }
  }
);

const logout = createAsyncThunk(
  'auth/logout',
  async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.warn('Logout API failed', error);
    } finally {
      tokenService.clearTokens();
    }
  }
);

const addFetchUserCases = (builder: ActionReducerMapBuilder<AuthState>) => {
  builder
    .addCase(fetchCurrentUser.pending, (state) => { state.isLoading = true; state.error = null; })
    .addCase(fetchCurrentUser.fulfilled, (state, action) => {
      state.user = action.payload; state.isAuthenticated = true; state.isLoading = false;
    })
    .addCase(fetchCurrentUser.rejected, (state) => {
      state.user = null; state.isAuthenticated = false; state.isLoading = false;
    });
};

const addLoginCases = (builder: ActionReducerMapBuilder<AuthState>) => {
  builder
    .addCase(login.pending, (state) => { state.isLoading = true; state.error = null; })
    .addCase(login.fulfilled, (state) => { state.isLoading = false; state.isAuthenticated = true; })
    .addCase(login.rejected, (state, action) => {
      state.isLoading = false; state.error = action.payload as string;
    });
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
    setLoading: (state, action: PayloadAction<boolean>) => { state.isLoading = action.payload; },
  },
  extraReducers: (builder) => {
    addFetchUserCases(builder);
    addLoginCases(builder);
    builder.addCase(logout.fulfilled, (state) => { state.user = null; state.isAuthenticated = false; });
  },
});

// Actions are defined in slice but only used internally via extraReducers

export default authSlice.reducer;
